import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Card, Reveal, Section, SectionHeader, StatusBadge } from "../deck/primitives";

const R = (risk, status, s, mitigation, gate) => ({ risk, status, s, mitigation, gate });
const RISKS = {
  "City / Legal": [
    R("Surplus Land Act review", "City Attorney review underway", "preliminary", "Support City counsel; structure as City-approved public-private transaction", "Legal pathway confirmed before major capital"),
    R("Exclusive rights not executed", "No ENA or equivalent", "nyc", "Pursue ENA or protected predevelopment agreement after Council direction", "Executed ENA before feasibility spend"),
    R("Ground lease terms unknown", "Not yet negotiated", "tbv", "Negotiate modest base rent + performance participation", "Term sheet before entitlement spend"),
    R("Assignment rights unknown", "Not yet negotiated", "tbv", "Seek assignment / JV transfer rights in ENA and ground lease", "Assignability confirmed before early-exit planning"),
  ],
  Site: [
    R("Legal description verification", "12.62 ac across 2 APNs · survey / title pending", "tbv", "ALTA survey, title report, City GIS reconciliation", "Survey + title before site plan"),
    R("Utility conditions", "Not yet studied", "tbv", "Civil engineering + utility will-serve letters", "Utility capacity confirmed before budget update"),
    R("Access / circulation", "Not yet studied", "tbv", "Traffic and circulation study", "Access plan accepted by City"),
    R("Infrastructure responsibility", "Not yet allocated", "tbv", "Negotiate public-improvement split in development agreement", "Responsibility matrix in term sheet"),
  ],
  Design: [
    R("Final rentable area + site plan not confirmed", "40,000 SF on 549,727 SF site · working allocation", "preliminary", "Architect area schedule and test-fit", "Confirmed area schedule before pricing"),
    R("Architect validation required", "Concept rendering only", "tbv", "Engage architect after protected control", "Schematic design accepted"),
  ],
  Construction: [
    R("No General Contractor pricing yet", "Conceptual budget only", "nyc", "Early GC preconstruction and value engineering", "GC pricing within budget tolerance"),
    R("No Guaranteed Maximum Price", "Not applicable at this stage", "nyc", "Negotiate GMP before construction close", "GMP executed before loan closing"),
    R("Site costs not fully known", "Working allowance", "preliminary", "Geotech, civil and utility studies", "Site cost validated in updated budget"),
  ],
  Leasing: [
    R("$36/SF rent requires broker validation", "Premium target", "tbv", "Retail broker leasing study by suite type", "Broker-validated rent before final investment decision"),
    R("Tenant demand must be validated", "No LOIs yet", "nyc", "Operator outreach and pre-leasing LOIs", "Pre-leasing threshold met before construction"),
    R("Leasing concessions unknown", "Not underwritten", "tbv", "Model TI / free rent by tenant category", "Concessions included in updated pro forma"),
  ],
  Capital: [
    R("No committed lender", "Working 65% debt assumption", "nyc", "Lender process after City control + feasibility", "Term sheet from construction lender"),
    R("No committed equity", "Working 35% equity assumption", "nyc", "JV partner selection and equity commitment", "Executed JV agreement"),
    R("Financing rates remain open", "Market-dependent", "preliminary", "Rate sensitivity; hedge strategy at closing", "Debt sized within coverage tests"),
  ],
  Valuation: [
    R("Final City ground rent must be deducted from NOI", "Not in base NOI", "tbv", "Model ground rent in Assumptions; negotiate structure", "Ground rent fixed before final valuation"),
    R("Market cap rates can change", "7.00% partner base", "preliminary", "Downside 7.50% sensitivity; conservative base", "Valuation re-tested at each gate"),
    R("Exit value depends on actual leasing", "No executed leases", "nyc", "Leasing-led development; stabilization before sale", "Stabilized occupancy achieved"),
  ],
};

export default function Risks() {
  const cats = Object.keys(RISKS);
  const [cat, setCat] = useState("All");
  const shown = cat === "All" ? cats : [cat];
  const total = cats.reduce((n, c) => n + RISKS[c].length, 0);
  return (
    <Section testid="section-risks">
      <SectionHeader num="12" eyebrow="Risks + Due Diligence" title="What still has to be proven." lede="Intentionally conservative. None of these items should be represented as complete until documented." />
      <Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3">
          {["All", ...cats].map((c) => (
            <button key={c} data-testid={`risk-filter-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`} onClick={() => setCat(c)}
              className={`rounded-xl border px-4 py-4 text-left transition-colors duration-200 ${cat === c ? "bg-ink border-ink text-white" : "bg-white border-line hover:border-arq/50"}`}>
              <div className={`font-display text-3xl tabular-nums ${cat === c ? "text-white" : "text-ink"}`}>{c === "All" ? total : RISKS[c].length}</div>
              <div className={`mt-1 text-xs ${cat === c ? "text-white/70" : "text-steel"}`}>{c}</div>
            </button>
          ))}
        </div>
      </Reveal>
      <AnimatePresence mode="wait">
        <motion.div key={cat} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.25 }}>
          <Card className="mt-6 overflow-x-auto">
            <table data-testid="risk-table" className="data-table w-full min-w-[920px]">
              <thead><tr><th className="w-[24%]">Risk</th><th className="w-[22%]">Current Status</th><th className="w-[28%]">Mitigation</th><th>Decision Gate</th></tr></thead>
              <tbody>
                {shown.map((c) => [
                  <tr key={c} className="hover:!bg-transparent"><td colSpan={4} className="!pt-7 !pb-3 font-mono text-[11px] tracking-[0.2em] text-arq uppercase">{c}</td></tr>,
                  ...RISKS[c].map((r) => (
                    <tr key={r.risk} data-testid={`risk-row-${r.risk.toLowerCase().replace(/[^a-z0-9]+/g, "-").slice(0, 40)}`}>
                      <td className="text-ink">{r.risk}</td>
                      <td><div className="mb-1.5"><StatusBadge s={r.s} /></div><div className="text-[13px] text-steel">{r.status}</div></td>
                      <td className="text-[13px] text-char">{r.mitigation}</td>
                      <td className="text-[13px] text-ink"><span className="inline-block h-1.5 w-1.5 rounded-full bg-arq mr-2 align-middle" />{r.gate}</td>
                    </tr>
                  )),
                ])}
              </tbody>
            </table>
          </Card>
        </motion.div>
      </AnimatePresence>
    </Section>
  );
}
